import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { PrimaryButton } from '@/components/PrimaryButton';
import { BuyerSelector } from '@/features/customers/components/BuyerSelector';
import type { CustomerItem } from '@/features/customers/types';
import {
  getDeviceById,
  sellInternalDevice,
  setDevicePublished,
  updateInternalDevice,
} from '@/features/operations/api';
import { ActiveReservationBlock } from '@/features/operations/components/ActiveReservationBlock';
import { CancelReservationDialog } from '@/features/operations/components/CancelReservationDialog';
import { CompleteReservationDialog } from '@/features/operations/components/CompleteReservationDialog';
import { ConditionConfirmDialog } from '@/features/operations/components/ConditionConfirmDialog';
import { ReserveDeviceDialog } from '@/features/operations/components/ReserveDeviceDialog';
import { RevertSaleDialog } from '@/features/operations/components/RevertSaleDialog';
import { DeviceChangeHistory } from '@/features/operations/components/DeviceChangeHistory';
import { DevicePayments } from '@/features/operations/components/DevicePayments';
import { NotesTimeline } from '@/features/operations/components/NotesTimeline';
import { DevicePhotoManager } from '@/features/operations/components/DevicePhotoManager';
import { colorAfterModelChange } from '@/features/operations/catalogue';
import {
  CatalogueModelFields,
  ColorField,
} from '@/features/operations/components/CatalogueModelFields';
import {
  ConditionFields,
  ConditionSummary,
} from '@/features/operations/components/ConditionFields';
import {
  buildConditionDraft,
  conditionAnswers,
  conditionDifferences,
  declaredBattery,
  type ConditionDifference,
  type ConditionDraft,
} from '@/features/operations/condition';
import {
  commercialModalityLabels,
  formatHistoryTimestamp,
  formatSaleDate,
  getDeviceStateLabel,
  paymentMethodLabels,
} from '@/features/operations/labels';
import { priceDifferenceLabel, priceError, toPriceInput } from '@/features/operations/money';
import { usePriceDisplay } from '@/features/operations/PriceDisplayProvider';
import { useNotes } from '@/features/operations/useNotes';
import type {
  BranchOption,
  DeviceDetail,
  DeviceModelOption,
  DeviceSheetSummary,
  QuotationRequestItem,
  UpdateDeviceInput,
} from '@/features/operations/types';
import { StatusChip } from '@/components/StatusChip';

type SheetDraft = {
  /// Empty only for a legacy device whose model matched no catalogue entry.
  phoneModelId: string;
  branchId: string;
  storageGb: string;
  batteryHealth: string;
  colorId: string;
  serialNumber: string;
  paidPrice: string;
  salePrice: string;
  commercialModality: string;
};

function buildDraft(device: DeviceSheetSummary): SheetDraft {
  return {
    phoneModelId: device.phoneModelId ?? '',
    branchId: device.branchId ?? '',
    storageGb: device.storageGb === null ? '' : String(device.storageGb),
    batteryHealth: device.batteryHealth === null ? '' : String(device.batteryHealth),
    colorId: device.color?.id ?? '',
    serialNumber: device.serialNumber || '',
    paidPrice: toPriceInput(device.paidPriceMinor),
    salePrice: toPriceInput(device.salePriceMinor),
    commercialModality: device.commercialModality ?? '',
  };
}

function SheetRow({ label, value }: { label: string; value: string }) {
  return (
    <Grid item xs={6} sm={4}>
      <Typography variant="caption" color="text.secondary" component="p">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Grid>
  );
}

/**
 * The internal sheet of a received device (#88): what PhoneTec recorded with the phone in hand,
 * next to what was declared about it.
 *
 * It opens read-only and is edited on purpose, through "Editar": the sheet stays editable in
 * every state, so a stray keystroke on a published device should not be one click from saved.
 * The state is shown but not changed here; which states exist and how they follow each other
 * is #91's.
 */
export function DeviceSheet({
  token,
  device,
  quotation,
  configuredFields = [],
  modelOptions,
  branchOptions,
  canRevertSale = false,
  onUpdated,
}: {
  token: string;
  device: DeviceSheetSummary;
  /// The request behind the phone: it carries what was declared, which the condition is shown
  /// and confirmed against.
  quotation: QuotationRequestItem | null;
  configuredFields?: import('@/features/operations/types').InspectionFieldSummary[];
  modelOptions: DeviceModelOption[];
  branchOptions: BranchOption[];
  canRevertSale?: boolean;
  /// `message` says what happened when it is more than an edit, like a reservation (#225, U-3).
  onUpdated: (device: DeviceDetail, message?: string) => void;
}) {
  const intakeFields = quotation?.inspectionFields ?? configuredFields;
  const declaredAnswers = quotation?.answers ?? [];
  const { formatNullablePrice } = usePriceDisplay();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<SheetDraft>(() => buildDraft(device));
  const [condition, setCondition] = useState<ConditionDraft>(() =>
    buildConditionDraft(intakeFields, device.inspection),
  );
  const deviceNotes = useNotes(token, { type: 'device', id: device.id });
  const [pendingDifferences, setPendingDifferences] = useState<ConditionDifference[] | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sellDialogOpen, setSellDialogOpen] = useState(false);
  const [saleDate, setSaleDate] = useState(() => new Date().toLocaleDateString('sv-SE'));
  const [saleBuyer, setSaleBuyer] = useState<CustomerItem | null>(null);
  const [saleError, setSaleError] = useState<string | null>(null);
  const [reserveDialogOpen, setReserveDialogOpen] = useState(false);
  const [closingReservation, setClosingReservation] = useState<'cancel' | 'complete' | null>(null);
  const [revertSaleOpen, setRevertSaleOpen] = useState(false);

  const setField = (field: keyof SheetDraft) => (value: string) =>
    setDraft((current) => ({ ...current, [field]: value }));

  const handleEdit = () => {
    setDraft(buildDraft(device));
    // A correction starts from what was already recorded -- the inspector's own work, not the
    // applicant's word.
    setCondition(buildConditionDraft(intakeFields, device.inspection));
    setError(null);
    setIsEditing(true);
  };

  const handlePublication = async (publish: boolean) => {
    setIsSaving(true);
    setError(null);
    try {
      onUpdated(await setDevicePublished(token, device.id, publish));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'No se pudo actualizar la publicación.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReserved = (reserved: DeviceDetail) => {
    setReserveDialogOpen(false);
    const made = reserved.activeReservation;
    onUpdated(
      reserved,
      made
        ? `Equipo reservado para ${made.buyer.name}. Vence el ${formatHistoryTimestamp(made.expiresAt)}`
        : undefined,
    );
    // The reservation left a system note on the device (BR-064).
    deviceNotes.reload();
  };

  /// Closing after the device was taken meanwhile reads it again, so the sheet shows its real
  /// state instead of a stale Publicado.
  const handleReserveClosed = async (stale: boolean) => {
    setReserveDialogOpen(false);
    if (!stale) return;
    try {
      onUpdated(await getDeviceById(token, device.id));
      deviceNotes.reload();
    } catch {
      setError('No pudimos actualizar la ficha. Recargá la página.');
    }
  };

  /// A reservation cancelled or concreted (VEN-4): the sheet shows the new state, and the action
  /// left a system note on the device (BR-064).
  const handleReservationClosed = (updated: DeviceDetail, message: string) => {
    setClosingReservation(null);
    onUpdated(updated, message);
    deviceNotes.reload();
  };

  /// Closing after the reservation changed meanwhile reads the device again, like reserving.
  const handleClosingDialogClosed = async (stale: boolean) => {
    setClosingReservation(null);
    if (!stale) return;
    try {
      onUpdated(await getDeviceById(token, device.id));
      deviceNotes.reload();
    } catch {
      setError('No pudimos actualizar la ficha. Recargá la página.');
    }
  };

  const reservationExpired = device.activeReservation
    ? new Date(device.activeReservation.expiresAt).getTime() <= Date.now()
    : false;

  const handleSell = async () => {
    if (!saleBuyer) return;
    setIsSaving(true);
    setSaleError(null);
    try {
      onUpdated(await sellInternalDevice(token, device.id, saleDate, saleBuyer.id));
      setSellDialogOpen(false);
      setSaleBuyer(null);
    } catch (cause) {
      setSaleError('No pudimos registrar la venta. Revisá el equipo y volvé a intentar.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaleReverted = (updated: DeviceDetail) => {
    setRevertSaleOpen(false);
    onUpdated(updated, 'Venta revertida. El equipo quedó pendiente de publicación.');
  };

  const handleRevertSaleClosed = async (stale: boolean) => {
    setRevertSaleOpen(false);
    if (!stale) return;
    try {
      onUpdated(await getDeviceById(token, device.id));
    } catch {
      setError('No pudimos actualizar la ficha. Recargá la página.');
    }
  };

  const handleSave = async () => {
    // A device that has a catalogue model can move to another one, never back to none.
    if (device.phoneModelId && !draft.phoneModelId) {
      setError('Elegí el modelo del equipo.');
      return;
    }

    if (!draft.branchId) {
      setError('Elegí en qué sucursal está el equipo.');
      return;
    }

    if (!draft.commercialModality) {
      setError('Elegí la modalidad comercial.');
      return;
    }

    const capacity = Number(draft.storageGb);

    if (!Number.isInteger(capacity) || capacity <= 0) {
      setError('La capacidad tiene que ser un número entero mayor que cero.');
      return;
    }

    const battery = draft.batteryHealth.trim() ? Number(draft.batteryHealth) : null;

    if (battery !== null && (!Number.isInteger(battery) || battery < 0 || battery > 100)) {
      setError('La batería tiene que ser un número entero entre 0 y 100.');
      return;
    }

    const priceProblem =
      (draft.paidPrice.trim() && priceError(draft.paidPrice, 'El precio pagado')) ||
      (draft.salePrice.trim() && priceError(draft.salePrice, 'El precio de venta'));

    if (priceProblem) {
      setError(priceProblem);
      return;
    }

    const differences = conditionDifferences(
      intakeFields,
      declaredAnswers,
      condition,
      draft.batteryHealth.trim(),
    );

    if (differences.length) {
      setPendingDifferences(differences);
      return;
    }

    await persist();
  };

  const persist = async () => {
    setIsSaving(true);
    setError(null);

    try {
      // The whole sheet is sent: an emptied optional field goes as null, which is what clears it
      // instead of leaving the old value in place. The condition rides along, because editing
      // the colour and correcting what was seen on the screen are one edit to whoever does it.
      const updated = await updateInternalDevice(token, device.id, {
        phoneModelId: draft.phoneModelId || undefined,
        branchId: draft.branchId,
        storageGb: Number(draft.storageGb),
        batteryHealth: draft.batteryHealth.trim() ? Number(draft.batteryHealth) : null,
        colorId: draft.colorId || null,
        serialNumber: draft.serialNumber.trim() || null,
        // A price sent unchanged leaves no entry in the history; only a real change does.
        paidPrice: draft.paidPrice.trim() || null,
        salePrice: draft.salePrice.trim() || null,
        commercialModality: draft.commercialModality as NonNullable<
          UpdateDeviceInput['commercialModality']
        >,
        answers: conditionAnswers(intakeFields, condition),
      });

      onUpdated(updated);
      setPendingDifferences(null);
      setIsEditing(false);
    } catch (cause) {
      setPendingDifferences(null);
      setError(cause instanceof Error ? cause.message : 'No pudimos guardar la ficha.');
    } finally {
      setIsSaving(false);
    }
  };

  const declaredPercent = declaredBattery(declaredAnswers);
  const declaredBatteryLabel =
    declaredPercent && declaredPercent.percentValue !== device.batteryHealth
      ? declaredPercent.answerLabel
      : null;

  const receivedLine = device.receivedBy
    ? `Recibido por ${device.receivedBy.fullName} el ${formatHistoryTimestamp(device.createdAt)}`
    : `Registrado el ${formatHistoryTimestamp(device.createdAt)}`;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="subtitle2">Estado del equipo</Typography>
        <StatusChip domain="device" status={device.deviceState} />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {receivedLine}
      </Typography>
      <Typography variant="body2">
        <strong>Ingreso:</strong>{' '}
        {device.origin === 'DIRECT_ENTRY' ? 'Ingreso directo, sin cotización' : 'Desde cotización'}
      </Typography>

      {device.soldAt ? (
        <Grid container spacing={1.5}>
          <SheetRow
            label="Fecha de venta"
            value={formatSaleDate(device.soldAt, device.sale?.paymentMethod)}
          />
          {device.sale ? (
            <>
              <SheetRow
                label="Precio final"
                value={formatNullablePrice(device.sale.finalPriceMinor)}
              />
              <SheetRow
                label="Medio de pago"
                value={
                  device.sale.paymentMethod
                    ? (paymentMethodLabels[device.sale.paymentMethod] ?? device.sale.paymentMethod)
                    : 'No informado'
                }
              />
              <SheetRow
                label="Diferencia con el publicado"
                value={
                  priceDifferenceLabel(
                    device.sale.finalPriceMinor,
                    device.sale.publishedPriceMinor,
                    formatNullablePrice,
                  ) ?? 'Sin precio publicado'
                }
              />
              <SheetRow
                label="Vendido por"
                value={
                  device.sale.soldBy?.fullName ??
                  (device.sale.paymentMethod === 'MERCADO_PAGO'
                    ? 'Compra online'
                    : 'Usuario eliminado')
                }
              />
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>Comprador:</strong>{' '}
                  {device.sale.buyer ? (
                    <>
                      {device.sale.accountId ? (
                        <Link
                          component={RouterLink}
                          to={`/admin/customers/${device.sale.accountId}`}
                        >
                          {device.sale.buyer.name}
                        </Link>
                      ) : (
                        device.sale.buyer.name
                      )}
                      {' · '}
                      {device.sale.buyer.email}
                    </>
                  ) : (
                    'No registrado'
                  )}
                </Typography>
              </Grid>
            </>
          ) : null}
          <SheetRow
            label="Garantía hasta"
            value={
              device.warrantyUntil
                ? formatSaleDate(device.warrantyUntil, device.sale?.paymentMethod)
                : 'No informada'
            }
          />
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" component="p">
              Condiciones de garantía
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {device.warrantyConditions ?? 'No informadas'}
            </Typography>
          </Grid>
        </Grid>
      ) : null}

      {canRevertSale && device.deviceState === 'SOLD' && device.sale ? (
        <Box>
          <Button
            variant="outlined"
            color="error"
            disabled={isSaving || isEditing}
            onClick={() => setRevertSaleOpen(true)}
          >
            Revertir venta
          </Button>
        </Box>
      ) : null}

      {canRevertSale && device.deviceState === 'SOLD' && !device.sale ? (
        <Alert severity="warning">
          Este equipo figura vendido, pero no tiene una venta vigente asociada. No se puede revertir
          hasta corregir ese dato.
        </Alert>
      ) : null}

      {device.saleHistory?.length ? (
        <Box>
          <Typography variant="overline" color="text.secondary">
            HISTORIAL DE VENTAS
          </Typography>
          <Stack component="ul" spacing={1.5} sx={{ listStyle: 'none', m: 0, p: 0, mt: 0.5 }}>
            {device.saleHistory.map((sale) => (
              <Stack component="li" key={sale.id} spacing={0.25}>
                <Typography variant="body2">
                  Venta del {formatSaleDate(sale.soldAt, sale.paymentMethod)} por{' '}
                  {formatNullablePrice(sale.finalPriceMinor)} ·{' '}
                  {sale.paymentMethod
                    ? (paymentMethodLabels[sale.paymentMethod] ?? sale.paymentMethod)
                    : 'Medio no informado'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Revertida el {formatHistoryTimestamp(sale.revertedAt!)} por{' '}
                  {sale.revertedBy?.fullName ?? 'Usuario eliminado'}: {sale.reversalReason}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      ) : null}

      {isEditing ? (
        <Grid container spacing={1.5}>
          {/* A legacy device with no catalogue model shows its old text until one is chosen. */}
          <CatalogueModelFields
            options={modelOptions}
            phoneModelId={draft.phoneModelId}
            emptyModelLabel={
              device.phoneModelId ? 'Elegí un modelo' : `Sin catálogo: ${device.model}`
            }
            onChange={(nextModelId) =>
              setDraft((current) => ({
                ...current,
                phoneModelId: nextModelId,
                // The colour the device already has stays only if the new model has it too, or
                // if the device goes back to the model it was received as.
                colorId:
                  nextModelId === device.phoneModelId
                    ? (device.color?.id ?? '')
                    : colorAfterModelChange(modelOptions, nextModelId, current.colorId),
              }))
            }
          />
          <Grid item xs={12} sm={6}>
            {/*
              Only branches PhoneTec still operates, plus the one this phone is already at: a
              device received before a branch closed has to keep naming it until it is moved.
            */}
            <TextField
              select
              fullWidth
              size="small"
              label="Sucursal"
              value={draft.branchId}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              onChange={(event) => setField('branchId')(event.target.value)}
            >
              <MenuItem value="">Elegí una sucursal</MenuItem>
              {branchOptions
                .filter((option) => option.isActive || option.id === device.branchId)
                .map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name} · {option.city}
                    {option.isActive ? '' : ' (cerrada)'}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Capacidad (GB)"
              value={draft.storageGb}
              onChange={(event) => setField('storageGb')(event.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Batería (%)"
              value={draft.batteryHealth}
              inputProps={{ min: 0, max: 100, step: 1 }}
              onChange={(event) => setField('batteryHealth')(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ColorField
              options={modelOptions}
              phoneModelId={draft.phoneModelId}
              colorId={draft.colorId}
              onChange={setField('colorId')}
              currentColor={device.color}
              currentPhoneModelId={device.phoneModelId}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              required
              fullWidth
              size="small"
              label="Modalidad comercial"
              value={draft.commercialModality}
              onChange={(event) => setField('commercialModality')(event.target.value)}
            >
              <MenuItem value="">Elegí una modalidad</MenuItem>
              <MenuItem value="DIRECT_PURCHASE">Compra directa</MenuItem>
              <MenuItem value="TRADE_IN">Parte de pago</MenuItem>
              <MenuItem value="CONSIGNMENT">Consignación</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Número de serie"
              value={draft.serialNumber}
              onChange={(event) => setField('serialNumber')(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Precio pagado (USD)"
              value={draft.paidPrice}
              inputProps={{ inputMode: 'decimal' }}
              helperText="En consignación, lo que se le paga al dueño al vender."
              onChange={(event) => setField('paidPrice')(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Precio de venta (USD)"
              value={draft.salePrice}
              inputProps={{ inputMode: 'decimal' }}
              helperText="Obligatorio para publicar."
              onChange={(event) => setField('salePrice')(event.target.value)}
            />
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={1.5}>
          <SheetRow label="Marca" value={device.brand} />
          <SheetRow label="Modelo" value={device.model} />
          {/* Null only for phones received before branches existed. */}
          <SheetRow
            label="Sucursal"
            value={
              device.branch
                ? `${device.branch.name} · ${device.branch.city}`
                : 'Sin sucursal (registro anterior)'
            }
          />
          <SheetRow
            label="Capacidad"
            value={device.storageGb === null ? 'No informada' : `${device.storageGb}GB`}
          />
          {/*
            The measured battery carries its declared counterpart when the two disagree. It is
            the value most likely to differ from what somebody declared from memory, and the
            confirmation at save time is no use to whoever reads the sheet afterwards.
          */}
          <SheetRow
            label="Batería"
            value={
              device.batteryHealth === null
                ? 'No informada'
                : `${device.batteryHealth}%${
                    declaredBatteryLabel ? ` (declarado ${declaredBatteryLabel})` : ''
                  }`
            }
          />
          {/* A colour deactivated after the phone was received is still the phone's colour. */}
          <SheetRow
            label="Color"
            value={
              device.color
                ? `${device.color.name}${device.color.isActive ? '' : ' (ya no se ofrece)'}`
                : 'No informado'
            }
          />
          <SheetRow label="Número de serie" value={device.serialNumber || 'No informado'} />
          <SheetRow
            label="Modalidad comercial"
            value={
              device.commercialModality
                ? commercialModalityLabels[device.commercialModality]
                : 'Sin definir'
            }
          />
          <SheetRow label="Precio pagado" value={formatNullablePrice(device.paidPriceMinor)} />
          <SheetRow label="Precio de venta" value={formatNullablePrice(device.salePriceMinor)} />
        </Grid>
      )}

      {/*
        The condition, in the same sheet: it was recorded in the same pass at the counter, so it
        is read and edited with the rest rather than as a section of its own.
      */}
      <Typography variant="subtitle2">Estado técnico y estético</Typography>
      {isEditing ? (
        <ConditionFields
          fields={intakeFields}
          declared={declaredAnswers}
          draft={condition}
          onChange={(fieldId, value) =>
            setCondition((current) => ({ ...current, [fieldId]: value }))
          }
        />
      ) : device.inspection ? (
        <ConditionSummary
          fields={intakeFields}
          declared={declaredAnswers}
          inspection={device.inspection}
        />
      ) : (
        <Typography variant="body2" color="text.secondary">
          No se registró el estado de este equipo.
        </Typography>
      )}

      {device.inspection?.evaluatedBy ? (
        <Typography variant="caption" color="text.secondary">
          Estado registrado por {device.inspection.evaluatedBy.fullName}
          {device.inspection.evaluatedAt
            ? ` el ${formatHistoryTimestamp(device.inspection.evaluatedAt)}`
            : ''}
        </Typography>
      ) : null}

      {error ? <Alert severity="error">{error}</Alert> : null}

      {device.deviceState === 'PENDING_PUBLICATION' || device.deviceState === 'PUBLISHED' ? (
        <Box>
          <PrimaryButton
            disabled={isSaving || isEditing}
            onClick={() => void handlePublication(device.deviceState === 'PENDING_PUBLICATION')}
          >
            {isSaving
              ? 'Guardando...'
              : device.deviceState === 'PENDING_PUBLICATION'
                ? 'Publicar'
                : 'Despublicar'}
          </PrimaryButton>
          {device.publishedAt && device.deviceState === 'PUBLISHED' ? (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Publicado el {formatHistoryTimestamp(device.publishedAt)}
            </Typography>
          ) : null}
        </Box>
      ) : null}

      {device.activeReservation ? (
        <ActiveReservationBlock reservation={device.activeReservation} />
      ) : null}

      {/* Only a reservation without payment is closed from here: the sheet's active reservation
          is never an online checkout, which the payment gateway or its expiry resolves (VEN-4). */}
      {device.deviceState === 'RESERVED' && device.activeReservation ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          {reservationExpired ? (
            <Typography variant="body2" color="text.secondary">
              Una reserva vencida no se puede concretar.
            </Typography>
          ) : (
            <Button
              variant="contained"
              color="success"
              disabled={isSaving || isEditing}
              onClick={() => setClosingReservation('complete')}
            >
              Concretar venta
            </Button>
          )}
          <Button
            variant="outlined"
            color="inherit"
            disabled={isSaving || isEditing}
            onClick={() => setClosingReservation('cancel')}
          >
            Cancelar reserva
          </Button>
        </Stack>
      ) : null}

      {device.activeReservation ? (
        <>
          <CancelReservationDialog
            key={`cancel-${device.activeReservation.id}`}
            open={closingReservation === 'cancel'}
            token={token}
            deviceId={device.id}
            reservation={device.activeReservation}
            onClose={(stale) => void handleClosingDialogClosed(stale)}
            onCancelled={(updated) =>
              handleReservationClosed(updated, 'Reserva cancelada. El equipo volvió a Publicado.')
            }
          />
          <CompleteReservationDialog
            key={`complete-${device.activeReservation.id}`}
            open={closingReservation === 'complete'}
            token={token}
            deviceId={device.id}
            publishedPriceMinor={device.salePriceMinor}
            reservation={device.activeReservation}
            onClose={(stale) => void handleClosingDialogClosed(stale)}
            onCompleted={(updated) => handleReservationClosed(updated, 'Venta registrada.')}
          />
        </>
      ) : null}

      {device.deviceState === 'PUBLISHED' ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="outlined"
            disabled={isSaving || isEditing}
            onClick={() => setReserveDialogOpen(true)}
          >
            Reservar
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={isSaving || isEditing}
            onClick={() => {
              setSaleDate(new Date().toLocaleDateString('sv-SE'));
              setSellDialogOpen(true);
            }}
          >
            Marcar vendido
          </Button>
        </Stack>
      ) : null}

      <ReserveDeviceDialog
        open={reserveDialogOpen}
        token={token}
        device={device}
        onClose={(stale) => void handleReserveClosed(stale)}
        onReserved={handleReserved}
      />

      {device.sale ? (
        <RevertSaleDialog
          open={revertSaleOpen}
          token={token}
          deviceId={device.id}
          sale={device.sale}
          onClose={(stale) => void handleRevertSaleClosed(stale)}
          onReverted={handleSaleReverted}
        />
      ) : null}

      <Dialog
        open={sellDialogOpen}
        onClose={() => {
          if (!isSaving) {
            setSellDialogOpen(false);
            setSaleBuyer(null);
            setSaleError(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Marcar equipo como vendido</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {saleError ? <Alert severity="error">{saleError}</Alert> : null}
            <Typography variant="body2" sx={{ mb: 2 }}>
              Se guardará la garantía configurada actualmente para este equipo.
            </Typography>
            <BuyerSelector token={token} buyer={saleBuyer} onChange={setSaleBuyer} />
            <TextField
              fullWidth
              type="date"
              label="Fecha de venta"
              value={saleDate}
              InputLabelProps={{ shrink: true }}
              onChange={(event) => setSaleDate(event.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            disabled={isSaving}
            onClick={() => {
              setSellDialogOpen(false);
              setSaleBuyer(null);
              setSaleError(null);
            }}
          >
            Cancelar
          </Button>
          <PrimaryButton
            disabled={isSaving || !saleDate || !saleBuyer}
            onClick={() => void handleSell()}
          >
            {isSaving ? 'Guardando...' : 'Confirmar venta'}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      <Box>
        {isEditing ? (
          <Stack direction="row" spacing={1}>
            <PrimaryButton onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Guardando...' : 'Guardar'}
            </PrimaryButton>
            <Button
              color="inherit"
              onClick={() => {
                setIsEditing(false);
                setError(null);
              }}
              disabled={isSaving}
            >
              Cancelar
            </Button>
          </Stack>
        ) : (
          <Button variant="outlined" color="inherit" onClick={handleEdit}>
            Editar
          </Button>
        )}
      </Box>

      <DevicePayments
        key={`${device.deviceState}:${device.sale?.id ?? 'none'}`}
        token={token}
        deviceId={device.id}
      />

      <DeviceChangeHistory entries={device.changeHistory} />
      {device.statusHistory?.length ? (
        <Box>
          <Typography variant="subtitle2">Historial de estado</Typography>
          {device.statusHistory.map((entry) => (
            <Stack key={entry.id} spacing={0.25}>
              <Typography variant="body2" color="text.secondary">
                {entry.fromStatus ? `${getDeviceStateLabel(entry.fromStatus)} → ` : 'Creado en '}
                {getDeviceStateLabel(entry.toStatus)} · {entry.changedBy?.fullName ?? 'Sistema'} ·{' '}
                {formatHistoryTimestamp(entry.createdAt)}
              </Typography>
              {entry.reason ? (
                <Typography variant="caption" color="text.secondary">
                  Motivo: {entry.reason}
                </Typography>
              ) : null}
            </Stack>
          ))}
        </Box>
      ) : null}

      <DevicePhotoManager token={token} deviceId={device.id} />

      <NotesTimeline
        notes={deviceNotes.notes}
        isLoading={deviceNotes.isLoading}
        error={deviceNotes.error}
        onAdd={deviceNotes.add}
        onEdit={deviceNotes.edit}
      />

      <ConditionConfirmDialog
        differences={pendingDifferences}
        isSaving={isSaving}
        onCancel={() => setPendingDifferences(null)}
        onConfirm={persist}
      />
    </Stack>
  );
}
